import { indexBy } from "diagnostics-lib/iterable";
import { useManualFailures } from "./useManualFailures";
import { useCallback, useMemo } from "react";
import useActiveAnalysesStream from "./useActiveAnalysesStream";
import { useQuery } from "@tanstack/react-query";
import usePerformanceIndicators from "./usePerformanceIndicators";
import { useGet } from "./useCall";
import { mapRemoteManualFaultEvent } from "./useManualFaultEvents";
import {
  MANUAL_FAULT_EVENT_PATH as PATH,
  useManualFaultEventsChannel,
} from "./useManualFaultEventsChannel";

export function useRemoteManualFaultEvent(projectId: string, manualFaultEventId: string) {
  const queryKey = useMemo(
    () => [PATH, projectId, manualFaultEventId],
    [projectId, manualFaultEventId],
  );
  const getRemoteManualFaultEvent = useGet(PATH);
  const queryFn = useCallback(
    () => getRemoteManualFaultEvent({ routeParams: { projectId, manualFaultEventId } }),
    [projectId, manualFaultEventId, getRemoteManualFaultEvent],
  );

  const { data } = useQuery({
    queryKey,
    queryFn,
    suspense: true,
  });

  useManualFaultEventsChannel(projectId);

  return useMemo(() => ({ data: data?.body }), [data?.body]);
}

export function useManualFaultEvent(projectId: string, manualFaultEventId: string) {
  const { data: manualFaultEvent } = useRemoteManualFaultEvent(projectId, manualFaultEventId);
  const manualFailures = useManualFailures(projectId);

  const indexedManualFailures = useMemo(
    () => indexBy(manualFailures, manualFailure => manualFailure.id),
    [manualFailures],
  );
  const { activeAnalyses } = useActiveAnalysesStream(projectId);
  const activeAnalysisDates = useMemo(
    () => activeAnalyses.map(a => new Date(a.date)),
    [activeAnalyses],
  );

  const performanceIndicators = usePerformanceIndicators(projectId);

  const data = useMemo(
    () =>
      manualFaultEvent
        ? mapRemoteManualFaultEvent({
            manualFaultEvent,
            indexedManualFailures,
            activeAnalysisDates,
            performanceIndicators,
          })
        : undefined,
    [activeAnalysisDates, indexedManualFailures, performanceIndicators, manualFaultEvent],
  );

  return useMemo(() => ({ data }), [data]);
}
